import { useState, useEffect } from "react";
import { GitPullRequest, ClipboardCheck, ChefHat, Star, TrendingUp, DoorOpen, Award, MapPin, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import { useResource, fmt, money, timeAgo, HubRow, SubHeader, StatTile, PillTabs, EmptyState, SearchInput, PersonRow } from "./Shared";
import { ConfirmButton, runAction, UserDialog } from "../AdminActions";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";

const HUB = [
  { key: "pipeline", icon: GitPullRequest, title: "Creator Pipeline", subtitle: "Signups → verifications → documents → approved. Review IDs, permits and approve here" },
  { key: "listings", icon: ClipboardCheck, title: "Listings", subtitle: "Review, approve or deny new and edited dishes before they go live" },
  { key: "all", icon: ChefHat, title: "All Creators", subtitle: "Filter every creator by county, type and selling status" },
  { key: "reviews", icon: Star, title: "Reviews", subtitle: "Read and moderate customer reviews" },
  { key: "performance", icon: TrendingUp, title: "Performance", subtitle: "Top kitchens, fulfillment and cancellation rates" },
  { key: "leaving", icon: DoorOpen, title: "Leaving Insights", subtitle: "Why creators pause or close their kitchens" },
  { key: "badges", icon: Award, title: "Badges", subtitle: "Define Top Rated, Veteran, Fast Responder… and see who holds them" },
  { key: "waitlist", icon: MapPin, title: "Creator Waitlist", subtitle: "Creators outside California, grouped by state" },
];

function Pipeline({ onBack, focusStage }) {
  const { data, loading } = useResource("creators-pipeline");
  const [stage, setStage] = useState(focusStage || null);
  const [reviewId, setReviewId] = useState(null);
  if (loading || !data) return <p className="py-16 text-center text-sm text-charcoal/40">Loading…</p>;

  if (reviewId) return <CreatorReview vendorId={reviewId} onBack={() => setReviewId(null)} />;

  const active = stage || "signups";
  const stageMeta = data.stages.find((s) => s.key === active);
  const people = data.creators.filter((c) => c.stage === active);
  const help = data.creators.filter((c) => c.needs_help);

  return (
    <div data-testid="creator-pipeline">
      <SubHeader title="Creator Pipeline" onBack={onBack} />
      <div className="grid grid-cols-3 gap-2.5">
        {data.stages.map((s, i) => (
          <StatTile key={s.key} n={data.counts[s.key] ?? 0} label={s.label} active={active === s.key} testId={`stage-${s.key}`}
            onClick={() => setStage(s.key)} />
        ))}
      </div>
      {stageMeta?.hint && (
        <p className="mt-3 rounded-xl bg-black/[0.04] px-4 py-3 text-[13px] text-charcoal/60">{stageMeta.hint}</p>
      )}
      <div className="mt-4 flex items-center justify-between">
        <span className="text-sm font-bold text-charcoal">{people.length} {people.length === 1 ? "person" : "people"}</span>
        <button onClick={() => setStage("needs_help")} data-testid="needs-help-filter"
          className={`rounded-full border px-3 py-1.5 text-xs font-bold ${active === "needs_help" ? "border-terracotta bg-terracotta text-white" : "border-terracotta/40 text-terracotta"}`}>
          Needs help ({help.length})
        </button>
      </div>
      <div className="mt-3 space-y-2.5">
        {(active === "needs_help" ? help : people).length === 0 ? (
          <EmptyState icon={ChefHat} text="Nobody at this step" />
        ) : (active === "needs_help" ? help : people).map((c) => (
          <PersonRow key={c.vendor_id} name={c.business_name || c.name}
            sub={`${c.email}${c.city ? " · " + c.city : ""}`}
            note={`${c.stage_label || ""}${c.checks_done != null ? ` · ${c.checks_done} checks` : ""}`}
            right={timeAgo(c.created_at)} testId={`pipeline-person-${c.vendor_id}`}
            onClick={() => setReviewId(c.vendor_id)} />
        ))}
      </div>
    </div>
  );
}

function CreatorReview({ vendorId, onBack }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(`/app-admin/detail/creator-review/${vendorId}`)
      .then(({ data }) => setData(data))
      .catch(() => setError("Could not load creator review"));
  }, [vendorId]);
  if (error) return <div><SubHeader title="Creator review" onBack={onBack} /><EmptyState text={error} /></div>;
  if (!data) return <p className="py-16 text-center text-sm text-charcoal/40">Loading…</p>;
  const p = data.profile || {};
  return (
    <div data-testid="creator-review">
      <SubHeader title={p.business_name || p.name || "Creator review"} subtitle={data.stage_label} onBack={onBack} />
      <div className="rounded-2xl border border-black/5 bg-white p-5">
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          {[["Contact", p.name], ["Legal name", p.legal_name], ["Email", p.email], ["Phone", p.phone],
            ["City", [p.city, p.state].filter(Boolean).join(", ")], ["County", p.county],
            ["Types", (p.business_types || []).join(", ")], ["Referral", p.referral_source]].map(([l, v]) => (
            <div key={l}><p className="text-[11px] font-bold uppercase tracking-wide text-charcoal/40">{l}</p>
              <p className="font-semibold text-charcoal">{v || "—"}</p></div>
          ))}
        </div>
      </div>
      <div className="mt-4 rounded-2xl border border-black/5 bg-white p-5" data-testid="review-checks">
        <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-charcoal/40">Checks · {data.checks_done}/{data.checks_total}</h3>
        <div className="space-y-2">
          {(data.checks || []).map((c) => (
            <div key={c.key} className="flex items-center gap-2.5 text-sm">
              {c.done ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-charcoal/25" />}
              <span className={c.done ? "text-charcoal" : "text-charcoal/50"}>{c.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-4 rounded-2xl border border-black/5 bg-white p-5" data-testid="review-docs">
        <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-charcoal/40">Documents · {data.docs_done}/{data.docs_total}</h3>
        <div className="space-y-2">
          {(data.docs || []).map((d) => (
            <div key={d.type} className="flex items-center justify-between text-sm">
              <span className="text-charcoal">{d.label}{d.required ? "" : " (optional)"}</span>
              <span className={`rounded-full px-2.5 py-1 text-xs font-bold capitalize ${d.status === "approved" ? "bg-emerald-100 text-emerald-700" : d.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-charcoal/5 text-charcoal/60"}`}>{d.status}</span>
            </div>
          ))}
        </div>
      </div>
      {data.needs_help && (
        <p className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-[13px] font-medium text-amber-800">
          Needs help: {(data.help_reasons || []).join(", ")}
        </p>
      )}
    </div>
  );
}

function Listings({ onBack }) {
  const [tab, setTab] = useState("pending");
  const [q, setQ] = useState("");
  const { data, loading, reload } = useResource(`listings?status=${tab}`, [tab]);
  const counts = data?.counts || {};
  const rows = (data?.listings || []).filter((l) =>
    !q || `${l.name || l.title || ""} ${l.vendor_name || ""}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="listings-screen">
      <SubHeader title="Listings" onBack={onBack} />
      <PillTabs value={tab} onChange={setTab} options={[
        { key: "pending", label: "To review", count: counts.pending },
        { key: "denied", label: "Denied", count: counts.denied },
        { key: "approved", label: "Live", count: counts.approved },
        { key: "all", label: "All" },
      ]} />
      <div className="mt-3"><SearchInput value={q} onChange={setQ} placeholder="Search dish, creator or category" testId="listings-search" /></div>
      <div className="mt-4 space-y-2.5">
        {loading ? <p className="py-10 text-center text-sm text-charcoal/40">Loading…</p> :
          rows.length === 0 ? <EmptyState icon={CheckCircle2} text={tab === "pending" ? "Nothing waiting for review." : "Nothing here."} /> :
          rows.map((l, i) => {
            const id = l.meal_id || l.listing_id || l.id;
            return (
              <div key={id || i} className="rounded-2xl border border-black/5 bg-white p-4" data-testid={`listing-row-${id || i}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[15px] font-bold text-charcoal">{l.name || l.title}</p>
                    <p className="mt-0.5 text-[13px] text-charcoal/55">{l.vendor_name} · {money(l.price)}</p>
                  </div>
                  {tab === "pending" && (
                    <div className="flex shrink-0 gap-2">
                      <ConfirmButton label="Approve" confirmLabel="Confirm?" variant="success" testId={`approve-listing-${id}`}
                        onConfirm={async () => { await runAction("listing_approve", id); toast.success("Listing approved"); reload(); }} />
                      <ConfirmButton label="Deny" confirmLabel="Confirm?" variant="danger" testId={`deny-listing-${id}`}
                        onConfirm={async () => { await runAction("listing_deny", id); toast.success("Listing denied"); reload(); }} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}

function AllCreators({ onBack }) {
  const { data, loading, reload } = useResource("users");
  const [q, setQ] = useState("");
  const [detailId, setDetailId] = useState(null);
  const creators = (data?.users || []).filter((u) => u.role === "vendor")
    .filter((u) => !q || `${u.name} ${u.email} ${u.city}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="all-creators">
      <SubHeader title="All Creators" subtitle="Every creator on the platform" onBack={onBack} />
      <SearchInput value={q} onChange={setQ} placeholder="Search name, email or city" testId="creators-search" />
      <div className="mt-4 space-y-2.5">
        {loading ? <p className="py-10 text-center text-sm text-charcoal/40">Loading…</p> :
          creators.length === 0 ? <EmptyState icon={ChefHat} text="No creators found." /> :
          creators.map((u) => (
            <div key={u.user_id} className="rounded-2xl border border-black/5 bg-white p-4" data-testid={`creator-row-${u.user_id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[15px] font-bold text-charcoal">{u.name}</p>
                  <p className="mt-0.5 truncate text-[13px] text-charcoal/55">{u.email}{u.city ? ` · ${u.city}` : ""}</p>
                  <p className="mt-1 text-[13px] text-charcoal/70">
                    <Star className="mr-1 inline h-3.5 w-3.5 fill-golden text-golden" />{u.rating || 0} ({u.review_count || 0})
                    {u.suspended && <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-700">Suspended</span>}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button onClick={() => setDetailId(u.user_id)} data-testid={`view-user-${u.user_id}`}
                    className="rounded-lg border border-black/10 px-3 py-1.5 text-xs font-bold text-charcoal/70 hover:bg-black/[0.03]">View</button>
                  {u.suspended ? (
                    <ConfirmButton label="Reactivate" confirmLabel="Confirm?" variant="success" testId={`reactivate-${u.user_id}`}
                      onConfirm={async () => { await runAction("user_status", u.user_id, { action: "reactivate" }); toast.success("Account reactivated"); reload(); }} />
                  ) : (
                    <ConfirmButton label="Suspend" confirmLabel="Confirm?" variant="danger" testId={`suspend-${u.user_id}`}
                      onConfirm={async () => { await runAction("user_status", u.user_id, { action: "suspend" }); toast.success("Account suspended"); reload(); }} />
                  )}
                </div>
              </div>
            </div>
          ))}
      </div>
      <UserDialog userId={detailId} onOpenChange={(o) => { if (!o) setDetailId(null); }} onAction={() => { setDetailId(null); reload(); }} />
    </div>
  );
}

function Reviews({ onBack, onlyDisputes = false }) {
  const { data, loading, reload } = useResource("reviews");
  const all = data?.reviews || [];
  const rows = onlyDisputes ? all.filter((r) => r.dispute) : all;
  return (
    <div data-testid="reviews-screen">
      <SubHeader title={onlyDisputes ? "Order Disputes" : "Reviews"} subtitle={onlyDisputes ? "Reviews disputed by creators" : "Every customer review"} onBack={onBack} />
      <div className="space-y-2.5">
        {loading ? <p className="py-10 text-center text-sm text-charcoal/40">Loading…</p> :
          rows.length === 0 ? <EmptyState icon={Star} text={onlyDisputes ? "No open disputes." : "No reviews yet."} /> :
          rows.map((r) => (
            <div key={r.review_id} className="rounded-2xl border border-black/5 bg-white p-4" data-testid={`review-row-${r.review_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-charcoal">{r.vendor_name}</p>
                  <p className="mt-0.5 text-[13px] text-charcoal/55">{r.customer_name} · {fmt(r.created_at)}</p>
                  <p className="mt-1.5 text-sm text-charcoal/80">
                    <Star className="mr-1 inline h-3.5 w-3.5 fill-golden text-golden" />{r.rating} — {r.comment}
                  </p>
                  {r.dispute && (
                    <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
                      Dispute ({r.dispute.status}): {r.dispute.reason}
                    </p>
                  )}
                </div>
                <ConfirmButton label="Delete" confirmLabel="Confirm?" variant="danger" testId={`delete-review-${r.review_id}`}
                  onConfirm={async () => { await runAction("review_delete", r.review_id); toast.success("Review deleted"); reload(); }} />
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

function Performance({ onBack }) {
  const { data, loading } = useResource("performance");
  if (loading || !data) return <p className="py-16 text-center text-sm text-charcoal/40">Loading…</p>;
  return (
    <div data-testid="performance-screen">
      <SubHeader title="Performance" subtitle="Fulfillment and cancellation rates" onBack={onBack} />
      <div className="grid grid-cols-3 gap-2.5">
        <StatTile n={data.counts.needs_attention} label="Needs attention" warn={data.counts.needs_attention > 0} testId="perf-attention" />
        <StatTile n={data.counts.exceptional} label="Exceptional" testId="perf-exceptional" />
        <StatTile n={data.counts.total} label="Total creators" testId="perf-total" />
      </div>
      <div className="mt-4 space-y-2.5">
        {data.vendors.map((v) => (
          <div key={v.vendor_id} className="rounded-2xl border border-black/5 bg-white p-4" data-testid={`perf-row-${v.vendor_id}`}>
            <div className="flex items-center justify-between">
              <p className="text-[15px] font-bold text-charcoal">{v.name}</p>
              {v.flag && v.flag !== "ok" && (
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${v.flag === "exceptional" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {v.flag === "exceptional" ? "Exceptional" : "Needs attention"}
                </span>
              )}
            </div>
            <p className="mt-1 text-[13px] text-charcoal/60">
              {v.orders} orders · {v.completed} completed · {v.cancelled} cancelled · {money(v.revenue)} · <Star className="mb-0.5 inline h-3 w-3 fill-golden text-golden" /> {v.rating}
            </p>
            {(v.reasons || []).length > 0 && (
              <p className="mt-1 text-xs text-amber-700">{v.reasons.join(" · ")}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Leaving({ onBack }) {
  const { data, loading } = useResource("leaving-insights");
  if (loading || !data) return <p className="py-16 text-center text-sm text-charcoal/40">Loading…</p>;
  const max = Math.max(1, ...data.reasons.map((r) => r.count));
  return (
    <div data-testid="leaving-screen">
      <SubHeader title="Leaving Insights" subtitle="Why creators pause or close their kitchens" onBack={onBack} />
      <div className="grid grid-cols-3 gap-2.5">
        <StatTile n={data.total} label="Total exits" testId="leaving-total" />
        <StatTile n={data.last_30_days} label="Last 30 days" testId="leaving-30d" />
        <StatTile n={data.by_role.vendor || 0} label="Creators" testId="leaving-creators" />
      </div>
      <div className="mt-4 space-y-2.5">
        {data.reasons.length === 0 ? <EmptyState icon={DoorOpen} text="No leaving reasons recorded." /> :
          data.reasons.map((r) => (
            <div key={r.reason} className="rounded-2xl border border-black/5 bg-white p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold text-charcoal">{r.reason || "No reason given"}</span>
                <span className="text-charcoal/55">{r.count} · {r.pct}%</span>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-black/5">
                <div className="h-full rounded-full bg-terracotta" style={{ width: `${(r.count / max) * 100}%` }} />
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

function Badges({ onBack }) {
  const { data, loading, reload } = useResource("badges");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", how_to: "", icon: "", criteria: {} });
  if (loading || !data) return <p className="py-16 text-center text-sm text-charcoal/40">Loading…</p>;

  const create = async () => {
    if (!form.name.trim()) { toast.error("Badge name is required"); return; }
    await runAction("badge_create", null, {
      name: form.name.trim(), description: form.description.trim(), how_to: form.how_to.trim(), icon: form.icon.trim(),
      criteria: form.criteria,
    });
    toast.success("Badge created");
    setShowForm(false);
    setForm({ name: "", description: "", how_to: "", icon: "", criteria: {} });
    reload();
  };

  return (
    <div data-testid="badges-screen">
      <SubHeader title="Badges" subtitle="Define recognition and see who holds it" onBack={onBack} />
      <div className="mb-4 flex justify-end">
        <button onClick={() => setShowForm(!showForm)} data-testid="new-badge-btn"
          className="rounded-lg bg-terracotta px-4 py-2 text-sm font-bold text-white hover:bg-terracotta-hover">
          {showForm ? "Cancel" : "New badge"}
        </button>
      </div>
      {showForm && (
        <div className="mb-4 space-y-3 rounded-2xl border border-black/5 bg-white p-5" data-testid="badge-form">
          <Input placeholder="Badge name (e.g. Top Rated)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="badge-name" />
          <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="badge-description" />
          <Input placeholder="How to earn it" value={form.how_to} onChange={(e) => setForm({ ...form, how_to: e.target.value })} data-testid="badge-howto" />
          <div className="grid grid-cols-3 gap-2">
            {(data.criteria_fields || []).map((f) => (
              <Input key={f.key} type="number" placeholder={f.label} data-testid={`badge-criteria-${f.key}`}
                onChange={(e) => setForm({ ...form, criteria: { ...form.criteria, [f.key]: Number(e.target.value) } })} />
            ))}
          </div>
          <ConfirmButton label="Create badge" confirmLabel="Confirm create?" variant="dark" testId="badge-create-btn" onConfirm={create} />
        </div>
      )}
      <div className="space-y-2.5">
        {data.badges.length === 0 ? <EmptyState icon={Award} text="No badges defined yet." /> :
          data.badges.map((b) => (
            <div key={b.badge_id} className="rounded-2xl border border-black/5 bg-white p-4" data-testid={`badge-row-${b.badge_id}`}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[15px] font-bold text-charcoal">{b.name}</p>
                  <p className="mt-0.5 text-[13px] text-charcoal/55">{b.description}</p>
                  <p className="mt-1 text-xs text-charcoal/45">
                    {b.holders} holders · {Object.entries(b.criteria || {}).map(([k, v]) => `${k.replace("min_", "min ")} ${v}`).join(" · ")}
                  </p>
                </div>
                <ConfirmButton label="Delete" confirmLabel="Confirm?" variant="danger" testId={`delete-badge-${b.badge_id}`}
                  onConfirm={async () => { await runAction("badge_delete", b.badge_id); toast.success("Badge deleted"); reload(); }} />
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

function Waitlist({ onBack }) {
  const { data, loading } = useResource("waitlist");
  if (loading || !data) return <p className="py-16 text-center text-sm text-charcoal/40">Loading…</p>;
  return (
    <div data-testid="waitlist-screen">
      <SubHeader title="Creator Waitlist" subtitle="Creators outside California, grouped by state" onBack={onBack} />
      {data.by_state.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {data.by_state.map((s) => (
            <span key={s.state} className="rounded-full bg-charcoal/5 px-3 py-1.5 text-xs font-bold text-charcoal/70">{s.state}: {s.count}</span>
          ))}
        </div>
      )}
      {data.rows.length === 0 ? <EmptyState icon={MapPin} text="No waitlist signups yet." /> : (
        <div className="space-y-2.5">
          {data.rows.map((w, i) => (
            <PersonRow key={w.id || i} name={w.email} sub={w.state} right={timeAgo(w.created_at)} testId={`waitlist-row-${i}`} />
          ))}
        </div>
      )}
    </div>
  );
}

export { Reviews as ReviewsScreen };

export default function CreatorsTab({ screen, goScreen }) {
  const [inner, setInner] = useState(null);
  const active = screen || inner;
  const back = () => (screen ? goScreen(null) : setInner(null));
  const open = screen ? goScreen : setInner;

  if (active === "pipeline") return <Pipeline onBack={back} />;
  if (active === "listings") return <Listings onBack={back} />;
  if (active === "all") return <AllCreators onBack={back} />;
  if (active === "reviews") return <Reviews onBack={back} />;
  if (active === "performance") return <Performance onBack={back} />;
  if (active === "leaving") return <Leaving onBack={back} />;
  if (active === "badges") return <Badges onBack={back} />;
  if (active === "waitlist") return <Waitlist onBack={back} />;

  return (
    <div data-testid="creators-tab">
      <h2 className="font-display text-2xl font-bold text-charcoal">Creators</h2>
      <p className="mb-5 mt-0.5 text-sm text-charcoal/55">Everything about your food creators</p>
      <div className="space-y-2.5">
        {HUB.map((h) => (
          <HubRow key={h.key} icon={h.icon} title={h.title} subtitle={h.subtitle} testId={`creators-hub-${h.key}`} onClick={() => open(h.key)} />
        ))}
      </div>
    </div>
  );
}
